import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openTicketCreateModal, toggleStats, toggleTicketFilters } from '../../../../../actions/ui';
import { exportRowsToCsv, exportRowsToExcel } from '../../../../../helpers/export-helper';
import { getTickets, searchTickets } from '../../../../../helpers/ticket-helper';
import { getTicketPriorityLabel, getTicketStatusLabel } from '../../../../../helpers/ticket-label-helper';

export const TaskListHeaderActions = () => {
    const dispatch = useDispatch();
    const { token } = useSelector((state) => state.auth);
    const { visibleTicketFilters, statsCollapsed, ticketFiltersCollapsed } = useSelector((state) => state.ui);

    const [isExportOpen, setIsExportOpen] = useState(false);
    const [isExporting, setIsExporting] = useState(false);

    const exportColumns = [
        { key: 'code', header: 'Código' },
        { key: 'title', header: 'Título' },
        { key: 'customer', header: 'Cliente' },
        { key: 'project', header: 'Proyecto' },
        { key: 'assignee', header: 'Asignado' },
        { key: 'priority', header: 'Prioridad' },
        { key: 'status', header: 'Estatus' },
        { key: 'dueRelative', header: 'Vence (relativo)' },
        { key: 'dueDate', header: 'Vence (fecha)' },
        { key: 'createdDate', header: 'Creado' },
    ];

    const handleToggleStats = () => {
        dispatch(toggleStats());
    };

    const handleToggleFilters = () => {
        dispatch(toggleTicketFilters());
    };

    const buildExportQueryParams = () => {
        const params = {};
        const searchField = String(visibleTicketFilters?.searchField || 'code');
        const searchTerm = String(visibleTicketFilters?.searchTerm || '').trim();

        if (visibleTicketFilters?.statusGroupFilter) {
            params.statusGroup = visibleTicketFilters.statusGroupFilter;
        } else if (visibleTicketFilters?.statusFilter && visibleTicketFilters.statusFilter !== 'ALL') {
            params.status = visibleTicketFilters.statusFilter;
        }

        if (visibleTicketFilters?.typeFilter && visibleTicketFilters.typeFilter !== 'ALL') {
            params.type = visibleTicketFilters.typeFilter;
        }

        if (visibleTicketFilters?.customerFilter && visibleTicketFilters.customerFilter !== 'ALL') {
            params.customer = visibleTicketFilters.customerFilter;
        }

        if (visibleTicketFilters?.projectFilter && visibleTicketFilters.projectFilter !== 'ALL') {
            params.project = visibleTicketFilters.projectFilter;
        }

        return {
            params,
            searchField,
            searchTerm,
        };
    };

    const fetchAllFilteredRows = async () => {
        if (!token) {
            return [];
        }

        const { params, searchField, searchTerm } = buildExportQueryParams();
        const hasSearch = Boolean(searchTerm);

        const initialResp = hasSearch
            ? await searchTickets(token, {
                ...params,
                field: searchField,
                value: searchTerm,
                limit: 1,
                offset: 0,
            })
            : await getTickets(token, {
                ...params,
                limit: 1,
                offset: 0,
            });

        if (!initialResp?.success) {
            return [];
        }

        const total = Number(initialResp.total || 0);
        if (!total) {
            return [];
        }

        const fullResp = hasSearch
            ? await searchTickets(token, {
                ...params,
                field: searchField,
                value: searchTerm,
                limit: total,
                offset: 0,
            })
            : await getTickets(token, {
                ...params,
                limit: total,
                offset: 0,
            });

        if (!fullResp?.success) {
            return [];
        }

        return Array.isArray(fullResp.data) ? fullResp.data : [];
    };

    const getNormalizedRows = (rows) => {
        return rows.map((ticket) => {
            const dueDate = ticket?.resolutionDueDate || ticket?.firstResponseDueDate;

            return {
                code: ticket?.code || '',
                title: ticket?.title || '',
                customer: ticket?.customer?.name || '',
                project: ticket?.project?.name || '',
                assignee: ticket?.assignee?.name || ticket?.assignee?.email || '',
                priority: getTicketPriorityLabel(ticket?.priority),
                status: getTicketStatusLabel(ticket?.status),
                dueRelative: dueDate ? new Date(dueDate).toLocaleString('es-MX') : '',
                dueDate: dueDate ? new Date(dueDate).toLocaleString('es-MX') : '',
                createdDate: ticket?.createdDate ? new Date(ticket.createdDate).toLocaleString('es-MX') : '',
            };
        });
    };

    const handleDownloadCsv = async () => {
        setIsExporting(true);
        const rawRows = await fetchAllFilteredRows();
        const rows = getNormalizedRows(rawRows);
        exportRowsToCsv({
            rows,
            columns: exportColumns,
            baseFileName: 'tickets_filtrados',
        });
        setIsExporting(false);
        setIsExportOpen(false);
    };

    const handleDownloadExcel = async () => {
        setIsExporting(true);
        const rawRows = await fetchAllFilteredRows();
        const rows = getNormalizedRows(rawRows);
        exportRowsToExcel({
            rows,
            columns: exportColumns,
            baseFileName: 'tickets_filtrados',
            sheetName: 'Tickets',
        });
        setIsExporting(false);
        setIsExportOpen(false);
    };

    const handleDocumentClick = (event) => {
        const exportDropdown = event.target.closest('.dropdown');
        if (!exportDropdown) {
            setIsExportOpen(false);
        }
    };

    const handleExportClick = () => {
        setIsExportOpen(!isExportOpen);
    };

    React.useEffect(() => {
        document.addEventListener('click', handleDocumentClick);
        return () => {
            document.removeEventListener('click', handleDocumentClick);
        };
    }, []);

    return (
        <div className="page-header-right ms-auto">
            <div className="page-header-right-items">
                <div className="d-flex align-items-center gap-2 page-header-right-items-wrapper">
                    <button type="button" className={`btn btn-icon btn-light-brand toolbar-toggle-btn ${statsCollapsed ? 'toolbar-toggle-btn--active' : ''}`} data-bs-toggle="collapse" data-bs-target="#collapseOne" aria-expanded={statsCollapsed} aria-controls="collapseOne" onClick={handleToggleStats}>
                        <i className="feather-bar-chart"></i>
                    </button>
                    <button type="button" className={`btn btn-icon btn-light-brand toolbar-toggle-btn ${ticketFiltersCollapsed ? 'toolbar-toggle-btn--active' : ''}`} onClick={handleToggleFilters}>
                        <i className="feather-filter"></i>
                    </button>
                    <div className="dropdown">
                        <button type="button" className="btn btn-icon btn-light-brand" data-bs-toggle="dropdown" data-bs-offset="0, 10" data-bs-auto-close="outside" aria-expanded="false" onClick={handleExportClick}>
                            <i className="feather-paperclip"></i>
                        </button>
                        <div className={`dropdown-menu dropdown-menu-end ${isExportOpen ? 'show' : ''}`}>
                            <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={handleDownloadCsv} disabled={!token || isExporting}>
                                <i className="feather-file"></i>
                                <span>{isExporting ? 'Descargando...' : 'CSV'}</span>
                            </button>
                            <button type="button" className="dropdown-item d-flex align-items-center gap-2" onClick={handleDownloadExcel} disabled={!token || isExporting}>
                                <i className="feather-grid"></i>
                                <span>{isExporting ? 'Descargando...' : 'Excel'}</span>
                            </button>
                        </div>
                    </div>

                    <button type="button" className="btn btn-primary" onClick={() => dispatch(openTicketCreateModal())}>
                        <i className="feather-plus me-2"></i>
                        <span>Nuevo</span>
                    </button>
                </div>
            </div>
        </div>
    );
};
